import { FunctionComponent } from "react";
import { Route, Switch } from "react-router-dom";
import { MenuBar } from "../../MenuBar";
import { ProtectedRoute } from "../../ProtectedRoute";
import { ProtectedCheckoutRoute } from "../../Routes";
import { SideBar } from "../../SideBar";
import { usePortal } from "../../providers/PortalProvider";
import { useViewer } from "../../providers/ViewerProvider";
import { LearningPath } from "../LearningPaths/LearningPath";
import { LearningPathLearnerSelection } from "../LearningPaths/LearningPathLearnerSelection";
import { BookingDetailPage } from "../Order/BookingDetailPage";
import { BookerIntention } from "../Order/BookingPage";
import { BrowseCatalog } from "./BrowseCatalog";
import { CatalogFilterContextProvider } from "./CatalogFilterContext";
import { CoordinatorCatalog } from "./CoordinatorCatalog";
import { Detail } from "./Detail";
import { DetailFilterContextProvider } from "./DetailFilterContext";
import { EventLearnerSelection } from "./EventLearnerSelection";
import { FindBookable } from "./FindBookable";
import { PrivateTraining } from "./PrivateTraining";
import { SelfRegistrationCatalog } from "./SelfRegistrationCatalog";

export const CatalogRoute: FunctionComponent = () => {
  const { isBookingPortal, isSelfRegistrationPortal } = usePortal();
  const { viewer } = useViewer();

  const allowCoordinatorFunctionality =
    !!viewer && viewer.isCoordinator && isBookingPortal;
  const allowRegistrationFunctionality =
    allowCoordinatorFunctionality || isSelfRegistrationPortal;

  return (
    <CatalogFilterContextProvider>
      <DetailFilterContextProvider>
        <Switch>
          <ProtectedRoute
            path="/catalog/:catalogType/event/:eventId/booking/:cartId"
            exact
            component={EventLearnerSelection}
            condition={allowRegistrationFunctionality}
          />
          <ProtectedRoute
            path="/catalog/:catalogType/learning-path/:learningPathId/booking/:cartId"
            exact
            component={LearningPathLearnerSelection}
            condition={allowRegistrationFunctionality}
          />
          <ProtectedCheckoutRoute
            path="/catalog/:catalogType/booking/:bookingId"
            exact
            component={BookingDetailPage}
            condition={allowRegistrationFunctionality}
          />
          <CatalogRoutesWithNavigation />
        </Switch>
      </DetailFilterContextProvider>
    </CatalogFilterContextProvider>
  );
};

export const CatalogRoutesWithNavigation: FunctionComponent = () => {
  const { isBookingPortal, isSelfRegistrationPortal, showPrivateTraining } =
    usePortal();
  const { viewer } = useViewer();

  const allowCoordinatorFunctionality =
    !!viewer && viewer.isCoordinator && isBookingPortal;
  const allowRegistrationFunctionality =
    allowCoordinatorFunctionality || isSelfRegistrationPortal;

  return (
    <CatalogFilterContextProvider>
      <DetailFilterContextProvider>
        <MenuBar />
        {!viewer?.isPreviewer && <SideBar />}
        <main className={viewer?.isPreviewer ? "preview" : "general"}>
          <Switch>
            <ProtectedRoute
              path={`/catalog/${BookerIntention.Self}`}
              exact
              component={SelfRegistrationCatalog}
              condition={allowRegistrationFunctionality}
            />
            <ProtectedRoute
              path={`/catalog/${BookerIntention.Coordinating}`}
              exact
              component={CoordinatorCatalog}
              condition={allowRegistrationFunctionality}
            />
            <ProtectedRoute
              path="/catalog/:catalogType/find"
              exact
              component={FindBookable}
              condition={allowRegistrationFunctionality}
            />
            {showPrivateTraining && (
              <ProtectedRoute
                path="/catalog/:catalogType/private"
                exact
                component={PrivateTraining}
                condition={allowRegistrationFunctionality}
              />
            )}
            <ProtectedRoute
              path="/catalog/:catalogType/course/:id"
              exact
              component={Detail}
              condition={allowRegistrationFunctionality}
            />
            <ProtectedRoute
              path="/catalog/:catalogType/learning-path/:learningPathId"
              exact
              component={LearningPath}
              condition={allowRegistrationFunctionality}
            />
            <Route component={BrowseCatalog} />
          </Switch>
        </main>
      </DetailFilterContextProvider>
    </CatalogFilterContextProvider>
  );
};
